"use client";

import { setupListeners } from "@reduxjs/toolkit/query";
import type { ReactNode } from "react";
import { useEffect, useRef } from "react";
import { Provider } from "react-redux";
import { AppStore, makeStore } from "./redux/store";
import { setActiveCompetitionsOnSync, setFeaturedImage } from "./redux/features/categories/slice";
import { GetActiveCompetitionsResponse } from "./components/ActiveCompetitions/types";
import { publishModalData, publishModalProps, publishModalVisibility } from "./redux/features/modal/modalSlice";
import { setIsRafEnabled, setHeaderIsLoading } from "./redux/features/header/headerSlice";
import { HeaderData } from "./(general-header)/header/types";
import SubscriptionPlansModal from "./(custom-header)/subscriber-competitions/components/subscriptionPlansModal";

interface Props {
  readonly children: ReactNode;
}

export const StoreProvider = ({ children }: Props) => {
  const storeRef = useRef<AppStore | null>(null);

  if (!storeRef.current) {
    // Create the store instance the first time this renders
    storeRef.current = makeStore();
  }

  useEffect(() => {
    if (storeRef.current != null) {
      // configure listeners using the provided defaults
      // optional, but required for `refetchOnFocus`/`refetchOnReconnect` behaviors
      const unsubscribe = setupListeners(storeRef.current.dispatch);
      return unsubscribe;
    }
  }, []);

  useEffect(() => {
    const store = storeRef.current;

    const onSync = (event: CustomEventInit<GetActiveCompetitionsResponse>) => {
      store.dispatch(setActiveCompetitionsOnSync(event.detail));
    };
    window.addEventListener("sync-header-store", onSync);

    const onRequest = () => {
      const { activeCompetitions, categories } = store.getState().competitionsCategories;
      if (activeCompetitions.length > 0) {
        window.dispatchEvent(
          new CustomEvent("sync-header-store", {
            detail: { competitions: activeCompetitions, categories },
          })
        );
      }
    };
    window.addEventListener("request-header-store", onRequest);

    window.dispatchEvent(new CustomEvent("request-header-store"));

    return () => {
      window.removeEventListener("sync-header-store", onSync);
      window.removeEventListener("request-header-store", onRequest);
    };
  }, []);

  useEffect(() => {
    const store = storeRef.current;

    const onSyncSecondStore = (event: CustomEventInit<HeaderData>) => {
      const headerData = event.detail;
      store.dispatch(setIsRafEnabled(headerData.IsRAFEnabled));
      store.dispatch(setFeaturedImage(headerData.featuredCategoryImage));
      store.dispatch(setHeaderIsLoading(false));
    };
    window.addEventListener("sync-second-store", onSyncSecondStore);

    const onRequestSecondStore = () => {
      const { IsRAFEnabled } = store.getState().header;
      const { featuredImage } = store.getState().competitionsCategories;
      if (IsRAFEnabled || featuredImage) {
        window.dispatchEvent(
          new CustomEvent("sync-second-store", {
            detail: { IsRAFEnabled, featuredCategoryImage: featuredImage } as Partial<HeaderData>,
          })
        );
      }
    };
    window.addEventListener("request-second-store", onRequestSecondStore);

    window.dispatchEvent(new CustomEvent("request-second-store"));

    return () => {
      window.removeEventListener("sync-second-store", onSyncSecondStore);
      window.removeEventListener("request-second-store", onRequestSecondStore);
    };
  }, []);

  useEffect(() => {
    const onOpenSubscription = () => {
      storeRef.current?.dispatch(publishModalData({ modalData: <SubscriptionPlansModal /> }));
      storeRef.current?.dispatch(publishModalProps({ withOverlay: false }));
      storeRef.current?.dispatch(publishModalVisibility({ isOpen: true }));
    };

    window.addEventListener("open-subscription-modal", onOpenSubscription);
    return () => window.removeEventListener("open-subscription-modal", onOpenSubscription);
  }, []);

  return <Provider store={storeRef.current}>{children}</Provider>;
};
